import { useTranslation } from "react-i18next";
import { APPLICATIONS } from "../../../constant/applications";
import { useDispatch, useSelector } from "react-redux";
import {
  selectApplicationForm,
  selectApplicationFormSignatures,
  selectCurrentApplication,
  selectEnvironment,
  selectIsApplicationFormModalVisible,
  selectUser
} from "../../../store/selector";
import { trackPendoEvent } from "../../../utils/pendo/pendoUtils";
import { ApplicationFormSignatureStatuses } from "../../../constant/ESign";
import ACTIONS from "../../../store/action";

const useStatuses = () => {
  const { t } = useTranslation();

  return {
    prepare: {
      key: 0,
      value: APPLICATIONS.statuses.prepare,
      name: t("patientApplications.prepare"),
      isSelectable: true
    },
    applied: {
      key: 1,
      value: APPLICATIONS.statuses.applied,
      name: t("patientApplications.applied"),
      isSelectable: true
    },
    applied_to_waitlist: {
      key: 2,
      value: APPLICATIONS.statuses.appliedToWaitlist,
      name: t("patientApplications.applied_to_waitlist"),
      isSelectable: true
    },
    approved: {
      key: 3,
      value: APPLICATIONS.statuses.approved,
      name: t("patientApplications.approved"),
      isSelectable: true
    },
    expired: {
      key: 4,
      value: APPLICATIONS.statuses.expired,
      name: t("patientApplications.expired")
    },
    canceled: {
      key: 5,
      value: APPLICATIONS.statuses.canceled,
      name: t("patientApplications.canceled"),
      isSelectable: true
    },
    rejected: {
      key: 6,
      value: APPLICATIONS.statuses.rejected,
      name: t("patientApplications.denied"),
      isSelectable: true
    },
    ineligible: {
      key: 7,
      value: APPLICATIONS.statuses.ineligible,
      name: t("patientApplications.ineligible"),
      isSelectable: true
    },
    notInterested: {
      key: 8,
      value: APPLICATIONS.statuses.notInterested,
      name: t("patientApplications.not_interested"),
      isSelectable: true
    },
    notReturned: {
      key: 9,
      value: APPLICATIONS.statuses.notReturned,
      name: t("patientApplications.not_returned"),
      isSelectable: true
    },
    exhausted: {
      key: 10,
      value: APPLICATIONS.statuses.exhausted,
      name: t("patientApplications.exhausted"),
      isSelectable: false
    },
    missingInformation: {
      key: 11,
      value: APPLICATIONS.statuses.missingInformation,
      name: t("patientApplications.missing_information"),
      isSelectable: true
    },
    temporarilyApproved: {
      key: 12,
      value: APPLICATIONS.statuses.temporarilyApproved,
      name: t("patientApplications.temporarily_approved"),
      isSelectable: true
    }
  };
};

const useInternalStatuses = () => {
  const { t } = useTranslation();

  return {
    prepare: {
      key: 0,
      value: APPLICATIONS.statuses.prepare,
      name: t("patientApplications.prepare"),
      isSelectable: true
    },
    applied: {
      key: 1,
      value: APPLICATIONS.statuses.applied,
      name: t("patientApplications.applied"),
      isSelectable: true
    },
    applied_to_waitlist: {
      key: 2,
      value: APPLICATIONS.statuses.appliedToWaitlist,
      name: t("patientApplications.applied_to_waitlist")
    },
    approved: {
      key: 3,
      value: APPLICATIONS.statuses.approved,
      name: t("patientApplications.approved")
    },
    expired: {
      key: 4,
      value: APPLICATIONS.statuses.expired,
      name: t("patientApplications.expired")
    },
    canceled: {
      key: 5,
      value: APPLICATIONS.statuses.canceled,
      name: t("patientApplications.canceled")
    },
    rejected: {
      key: 6,
      value: APPLICATIONS.statuses.rejected,
      name: t("patientApplications.denied")
    },
    ineligible: {
      key: 7,
      value: APPLICATIONS.statuses.ineligible,
      name: t("patientApplications.ineligible")
    },
    notInterested: {
      key: 8,
      value: APPLICATIONS.statuses.notInterested,
      name: t("patientApplications.not_interested")
    },
    notReturned: {
      key: 9,
      value: APPLICATIONS.statuses.notReturned,
      name: t("patientApplications.not_returned")
    },
    exhausted: {
      key: 10,
      value: APPLICATIONS.statuses.exhausted,
      name: t("patientApplications.exhausted"),
      isSelectable: false
    },
    missingInformation: {
      key: 11,
      value: APPLICATIONS.statuses.missingInformation,
      name: t("patientApplications.missing_information"),
      isSelectable: false
    },
    temporarilyApproved: {
      key: 12,
      value: APPLICATIONS.statuses.temporarilyApproved,
      name: t("patientApplications.temporarily_approved"),
      isSelectable: false
    }
  };
};

const useActiveStatuses = () => {
  const { t } = useTranslation();

  return {
    prepare: {
      key: 0,
      value: APPLICATIONS.statuses.prepare,
      name: t("patientApplications.prepare"),
      isSelectable: true
    },
    applied: {
      key: 1,
      value: APPLICATIONS.statuses.applied,
      name: t("patientApplications.applied"),
      isSelectable: true
    },
    applied_to_waitlist: {
      key: 2,
      value: APPLICATIONS.statuses.appliedToWaitlist,
      name: t("patientApplications.applied_to_waitlist"),
      isSelectable: true
    },
    approved: {
      key: 3,
      value: APPLICATIONS.statuses.approved,
      name: t("patientApplications.approved"),
      isSelectable: true
    },
    temporarilyApproved: {
      key: 12,
      value: APPLICATIONS.statuses.temporarilyApproved,
      name: t("patientApplications.temporarily_approved"),
      isSelectable: true
    },
    missingInformation: {
      key: 11,
      value: APPLICATIONS.statuses.missingInformation,
      name: t("patientApplications.missing_information"),
      isSelectable: true
    }
  };
};

const useInactiveStatuses = () => {
  const { t } = useTranslation();

  return {
    expired: {
      key: 4,
      value: APPLICATIONS.statuses.expired,
      name: t("patientApplications.expired")
    },
    canceled: {
      key: 5,
      value: APPLICATIONS.statuses.canceled,
      name: t("patientApplications.canceled"),
      isSelectable: true
    },
    rejected: {
      key: 6,
      value: APPLICATIONS.statuses.rejected,
      name: t("patientApplications.denied"),
      isSelectable: true
    },
    ineligible: {
      key: 7,
      value: APPLICATIONS.statuses.ineligible,
      name: t("patientApplications.ineligible"),
      isSelectable: true
    },
    notInterested: {
      key: 8,
      value: APPLICATIONS.statuses.notInterested,
      name: t("patientApplications.not_interested"),
      isSelectable: true
    },
    notReturned: {
      key: 9,
      value: APPLICATIONS.statuses.notReturned,
      name: t("patientApplications.not_returned"),
      isSelectable: true
    },
    exhausted: {
      key: 10,
      value: APPLICATIONS.statuses.exhausted,
      name: t("patientApplications.exhausted"),
      isSelectable: true
    }
  };
};

const useESignTrackingEvent = () => {
  const dispatch = useDispatch();
  const application = useSelector(selectCurrentApplication);
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const applicationForm = useSelector(selectApplicationForm);
  const applicationFormSignatures = useSelector(selectApplicationFormSignatures);

  const signaturesStatus = applicationFormSignatures?.map((sig) => {
    return {
      status: sig.status,
      signerType: sig.signerType
    };
  });
  const trackEvent = (eventName, extraData = {}) => {
    try {
      trackPendoEvent(dispatch, eventName, {
        applicationId: application?.id,
        programName: application?.displayName,
        providerName: application?.potentialSaving?.APProvider,
        priority: application?.potentialSaving?.priority,
        journey: application?.potentialSaving?.journey,
        associatedDrugs: application?.associatedDrugs,
        applicationStatus: application?.status,
        assistanceProgramId: application?.potentialSaving?.assistanceProgram,
        tmPatientId: application?.potentialSaving?.patient,
        pap: application?.pap,
        user: user?.email,
        applicationFormStatus: applicationForm?.status,
        fnFlow: applicationForm?.applicationTemplateName,
        supportPDF: applicationForm?.supportPDF,
        signaturesStatus: signaturesStatus,
        account,
        drugCui: application?.potentialSaving?.matchedDrugCui,
        drugName: application?.drugDisplayName,
        ...extraData
      });
    } catch (errorText) {
      console.error("Error dispatching enrollment Pendo event:", errorText);
    }
  };
  return { trackEvent };
};

const getWidgetRelevantData = (signerData, remoteRequest) => {
  let diffDetectionResult = {
    email: signerData.email,
    phone: signerData.phone,
    name: signerData.name,
    signerType: signerData.signerType,
    isSignerDataUpdatedOrAddedAfterRequest: false,
    isSignerDataUpdatedAfterRequest: false
  };

  if (!remoteRequest) {
    return diffDetectionResult;
  }

  const emailExistsInRemoteRequest = !!remoteRequest.email;
  const phoneExistsInRemoteRequest = !!remoteRequest.phone;

  const isNameChanged = (remoteRequest.name ?? "") !== (signerData.name ?? "");

  const isEmailChanged = (remoteRequest.email ?? "") !== (signerData.email ?? "");

  const isExistingEmailChanged = emailExistsInRemoteRequest && isEmailChanged;
  const isNewEmailAdded = !emailExistsInRemoteRequest && isEmailChanged;

  const isPhoneChanged = (remoteRequest.phone ?? "") !== (signerData.phone ?? "");
  const isExistingPhoneChanged = phoneExistsInRemoteRequest && isPhoneChanged;
  const isNewPhoneAdded = !phoneExistsInRemoteRequest && isPhoneChanged;

  const isSignerDataUpdatedAfterRequest = isExistingEmailChanged || isExistingPhoneChanged || isNameChanged;

  // "Added" means that data that did not exist in the remote request now exists in the patient data (whether it was added after the remote request or was existing before but was not chosen to be included in the remote request).
  const isSignerDataUpdatedOrAddedAfterRequest = isNewEmailAdded || isNewPhoneAdded || isSignerDataUpdatedAfterRequest;

  const shouldUseOldData = signerData.status !== ApplicationFormSignatureStatuses.EXPIRED;

  if (isSignerDataUpdatedOrAddedAfterRequest) {
    diffDetectionResult.isSignerDataUpdatedOrAddedAfterRequest = true;
  }

  if (isSignerDataUpdatedAfterRequest) {
    diffDetectionResult.isSignerDataUpdatedAfterRequest = true;
  }

  if (shouldUseOldData) {
    diffDetectionResult.email = remoteRequest.email;
    diffDetectionResult.phone = remoteRequest.phone;
    diffDetectionResult.name = remoteRequest.name;
    diffDetectionResult.signerType = remoteRequest.signerType;
  }

  return diffDetectionResult;
};

// this hook suppose to check if widget data changed, and return what data should be shown in the widget
const useESignSignerDataChange = (signerData) => {
  const remoteRequest = signerData.phiRemoteSignatureRequest;
  return getWidgetRelevantData(signerData, remoteRequest);
};

const useApplicationFormModalVisibility = () => {
  const dispatch = useDispatch();
  const isApplicationFormModalVisible = useSelector(selectIsApplicationFormModalVisible);

  const setIsApplicationFormModalVisible = (isVisible) => {
    dispatch(ACTIONS.setIsApplicationFormModalVisible(isVisible));
  };

  return {
    isApplicationFormModalVisible,
    setIsApplicationFormModalVisible
  };
};

export {
  useStatuses,
  useInternalStatuses,
  useActiveStatuses,
  useInactiveStatuses,
  useESignTrackingEvent,
  useESignSignerDataChange,
  getWidgetRelevantData,
  useApplicationFormModalVisibility
};
